import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Drawer, Input, Modal, Space, Table, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import { serverFetch } from "@core/server";
import AgentForm from "./agent";
import "./agents.css";

const errMsg = (e) => e?.message || "Ошибка";

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const Agents = () => {
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 40) === 1;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);

  const getData = useCallback(() => {
    setLoading(true);
    setError("");
    serverFetch("Agent")
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => {
        setError(errMsg(e));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(getData, [getData]);

  const filtered = useMemo(
    () =>
      items.filter((item) => {
        if (!showHidden && !item.active) return false;
        const q = searchText.trim().toLowerCase();
        if (!q) return true;
        return `${item.name || ""} ${item.url || ""}`.toLowerCase().includes(q);
      }),
    [items, showHidden, searchText]
  );

  const openCreate = () => {
    if (!canEdit) return;
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.id == null) return;
    setSelectedId(row.id);
    setDrawerId(row.id);
    setDrawerOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Удалить агента?",
      content: "Это действие нельзя отменить.",
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        serverFetch(`Agent/${record.id}`, { method: "DELETE" })
          .then(() => {
            message.success("Агент удалён");
            if (drawerId === record.id) setDrawerOpen(false);
            setSelectedId(null);
            return getData();
          })
          .catch((e) => {
            message.error(errMsg(e));
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 70 },
    { title: "Наименование", dataIndex: "name" },
    { title: "URL", dataIndex: "url", ellipsis: true },
    {
      title: "Акт",
      dataIndex: "active",
      width: 56,
      align: "center",
      render: (v) => <Checkbox checked={!!v} disabled />
    }
  ];

  if (!canEdit) {
    return (
      <div className="msg-agents">
        <Alert type="warning" showIcon message="Нет права на настройки агентов" />
      </div>
    );
  }

  return (
    <div className="msg-agents">
      {error ? <Alert className="msg-agents__error" type="error" showIcon message={error} /> : null}
      <div className="msg-agents__toolbar">
        <Space size={4}>
          <Tooltip title="Добавить">
            <Button type="text" icon={<PlusOutlined />} onClick={openCreate} />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              disabled={!selectedId}
              onClick={() => {
                const row = items.find((i) => i.id === selectedId);
                if (row) handleDelete(row);
              }}
            />
          </Tooltip>
          <Tooltip title="Обновить">
            <Button type="text" icon={<ReloadOutlined />} onClick={getData} />
          </Tooltip>
        </Space>
        <Input
          className="msg-agents__filter"
          placeholder="Поиск"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Checkbox checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)}>
          Показывать неактивные
        </Checkbox>
      </div>
      <Table
        className="msg-agents__table"
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        onRow={(record) => ({
          onClick: () => setSelectedId(record.id),
          onDoubleClick: () => openEdit(record)
        })}
        rowClassName={(record) => (record.id === selectedId ? "msg-agents__row--selected" : "")}
      />
      <Drawer
        title={drawerId ? "Агент" : "Новый агент"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        destroyOnHidden
      >
        <AgentForm
          id={drawerId}
          onClose={(refresh) => {
            setDrawerOpen(false);
            if (refresh) getData();
          }}
        />
      </Drawer>
    </div>
  );
};

export default Agents;
